import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useEditorContext } from './context/editor-provider';

const {
  CKEditor: { scrollViewportToShowTarget },
  CKEditorPremiumFeatures: { Suggestion: CKEditorSuggestion },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
});

type Suggestion = InstanceType<typeof CKEditorSuggestion>;
const FOCUSED_CLASS = 'finch-track-change-focused';

/**
 * Helper to get only suggestions that still have items in the editor.
 */
function getActiveSuggestions(
  editor: ReturnType<typeof useEditorContext>['editor'],
): Suggestion[] {
  if (!editor) return [];
  try {
    const trackChangesPlugin = editor.plugins.get('TrackChanges');
    const suggestions = trackChangesPlugin.getSuggestions();
    return suggestions.filter(
      (suggestion: Suggestion) => suggestion.getItems().length > 0,
    );
  } catch {
    return [];
  }
}

export const TrackChangesControls = () => {
  const { editor } = useEditorContext();
  const [hasTrackedChanges, setHasTrackedChanges] = useState(false);
  const [changeCount, setChangeCount] = useState(0);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs to track state inside callbacks without causing effect re-registration.
  const isProcessingRef = useRef(false);
  const currentChangeIndexRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  useEffect(() => {
    currentChangeIndexRef.current = currentChangeIndex;
  }, [currentChangeIndex]);

  const syncSuggestionState = useCallback(() => {
    if (!editor) return;
    const activeSuggestions = getActiveSuggestions(editor);
    setHasTrackedChanges(activeSuggestions.length > 0);
    setChangeCount(activeSuggestions.length);

    // Reset index if it's out of bounds
    if (
      currentChangeIndexRef.current >= activeSuggestions.length &&
      activeSuggestions.length > 0
    ) {
      setCurrentChangeIndex(0);
    }
  }, [editor]);

  const clearFocusedHighlight = useCallback(() => {
    if (!editor) {
      return;
    }

    const editable = editor.editing.view.getDomRoot();
    if (!editable) {
      return;
    }

    editable
      .querySelectorAll(`.ck-suggestion-marker.${FOCUSED_CLASS}`)
      .forEach((node) => {
        node.classList.remove(FOCUSED_CLASS);
      });
  }, [editor]);

  useEffect(() => {
    if (!editor) return undefined;

    const debouncedCheck = () => {
      // Skip updates while batch-processing accept/reject to avoid
      // an event storm that freezes the UI.
      if (isProcessingRef.current) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        syncSuggestionState();
      }, 150);
    };

    // Initial check (immediate, not debounced)
    syncSuggestionState();

    editor.model.document.on('change:data', debouncedCheck);

    return () => {
      editor.model.document.off('change:data', debouncedCheck);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      clearFocusedHighlight();
    };
    // Only re-register when the editor instance changes.
    // currentChangeIndex is tracked via ref to avoid effect re-registration.
  }, [editor, clearFocusedHighlight, syncSuggestionState]);

  function scrollToSuggestion(suggestion: Suggestion, alignToTop = false) {
    if (!editor) {
      return;
    }

    const modelRange = suggestion.getFirstRange();
    if (!modelRange) return;

    const viewRange = editor.editing.mapper.toViewRange(modelRange);
    const domRange = editor.editing.view.domConverter.viewRangeToDom(viewRange);
    if (!domRange) return;

    // Clear previous highlight
    clearFocusedHighlight();

    // Highlight all ck-suggestion-marker spans that overlap this suggestion
    const editable = editor.editing.view.getDomRoot();
    if (editable) {
      const treeWalker = document.createTreeWalker(
        editable,
        NodeFilter.SHOW_ELEMENT,
        {
          acceptNode: (node) =>
            (node as HTMLElement).classList?.contains('ck-suggestion-marker')
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_SKIP,
        },
      );

      let node = treeWalker.nextNode();
      while (node) {
        if (domRange.intersectsNode(node)) {
          (node as HTMLElement).classList.add(FOCUSED_CLASS);
        }
        node = treeWalker.nextNode();
      }
    }

    // Use CKEditor's scroll utility — it understands the editor's nested scroll containers.
    // A large viewportOffset pushes the target toward the center of the visible area.
    scrollViewportToShowTarget({
      target: domRange,
      viewportOffset: Math.round(window.innerHeight / 3),
    });
  }

  const getSuggestionsWithItems = (): Suggestion[] => {
    if (!editor) return [];
    const suggestions = editor.plugins.get('TrackChanges').getSuggestions();
    return suggestions.filter(
      (suggestion) => suggestion.getItems().length > 0,
    );
  };

  const handlePreviousChange = () => {
    const filtered = getSuggestionsWithItems();
    if (filtered.length === 0) return;

    const newIndex =
      currentChangeIndex > 0 ? currentChangeIndex - 1 : filtered.length - 1;

    const suggestions = getActiveSuggestions(editor);

    const suggestion = suggestions[newIndex];
    if (!suggestion) {
      return;
    }

    scrollToSuggestion(suggestion);
    setCurrentChangeIndex(newIndex);
  };

  const handleNextChange = () => {
    const filtered = getSuggestionsWithItems();
    if (filtered.length === 0) return;

    const newIndex =
      currentChangeIndex < changeCount - 1 ? currentChangeIndex + 1 : 0;

    if (!editor) {
      return;
    }

    const suggestions = getActiveSuggestions(editor);

    const suggestion = filtered[newIndex];
    if (!suggestion) return;

    scrollToSuggestion(suggestion);
    setCurrentChangeIndex(newIndex);
  };

  const advanceAfterAction = (prevIndex: number) => {
    const remaining = getSuggestionsWithItems();
    if (remaining.length === 0) {
      setHasTrackedChanges(false);
      clearFocusedHighlight();
      return;
    }
    const nextIndex = prevIndex >= remaining.length ? 0 : prevIndex;
    setCurrentChangeIndex(nextIndex);
    scrollToSuggestion(remaining[nextIndex]);
  };

  const handleAcceptCurrent = () => {
    const filtered = getSuggestionsWithItems();
    const suggestion = filtered[currentChangeIndex];
    if (!suggestion || isProcessing) return;

    suggestion.accept();
    advanceAfterAction(currentChangeIndex);
  };

  const handleRejectCurrent = () => {
    const filtered = getSuggestionsWithItems();
    const suggestion = filtered[currentChangeIndex];
    if (!suggestion || isProcessing) return;

    suggestion.discard();
    advanceAfterAction(currentChangeIndex);
  };

  const handleAcceptAll = async () => {
    if (!editor || isProcessing) return;

    setIsProcessing(true);
    isProcessingRef.current = true;
    try {
      // Process suggestions one at a time, re-fetching after each accept
      // to avoid operating on stale/detached suggestion objects.
      const failedIds = new Set<string>();
      let remaining = getActiveSuggestions(editor).filter(
        (s) => !failedIds.has(s.id),
      );
      let lastAttemptedId: string | null = null;
      // eslint-disable-next-line no-await-in-loop
      while (remaining.length > 0) {
        const suggestion = remaining[0];
        // If the same suggestion survived the previous accept, skip it
        if (suggestion.id === lastAttemptedId) {
          failedIds.add(suggestion.id);
          remaining = getActiveSuggestions(editor).filter(
            (s) => !failedIds.has(s.id),
          );
          lastAttemptedId = null;
          // eslint-disable-next-line no-continue
          continue;
        }
        lastAttemptedId = suggestion.id;
        try {
          suggestion.accept();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error accepting suggestion, skipping:', err);
          failedIds.add(suggestion.id);
        }
        // Brief yield to let CKEditor update its model
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 50);
        });
        // Re-fetch fresh suggestions after each operation, excluding failed ones
        remaining = getActiveSuggestions(editor).filter(
          (s) => !failedIds.has(s.id),
        );
      }

      setHasTrackedChanges(false);
      setChangeCount(0);
    } catch (error) {
      console.error('Error accepting all changes:', error);
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;
      // Sync state after processing completes
      syncSuggestionState();
    }
  };

  const handleRejectAll = async () => {
    if (!editor || isProcessing) return;

    setIsProcessing(true);
    isProcessingRef.current = true;
    try {
      // Process suggestions one at a time, re-fetching after each discard
      // to avoid operating on stale/detached suggestion objects.
      const failedIds = new Set<string>();
      let remaining = getActiveSuggestions(editor).filter(
        (s) => !failedIds.has(s.id),
      );
      let lastAttemptedId: string | null = null;
      // eslint-disable-next-line no-await-in-loop
      while (remaining.length > 0) {
        const suggestion = remaining[0];
        // If the same suggestion survived the previous discard, skip it
        if (suggestion.id === lastAttemptedId) {
          failedIds.add(suggestion.id);
          remaining = getActiveSuggestions(editor).filter(
            (s) => !failedIds.has(s.id),
          );
          lastAttemptedId = null;
          // eslint-disable-next-line no-continue
          continue;
        }
        lastAttemptedId = suggestion.id;
        try {
          suggestion.discard();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error discarding suggestion, skipping:', err);
          failedIds.add(suggestion.id);
        }
        // Brief yield to let CKEditor update its model
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 50);
        });
        // Re-fetch fresh suggestions after each operation, excluding failed ones
        remaining = getActiveSuggestions(editor).filter(
          (s) => !failedIds.has(s.id),
        );
      }

      setHasTrackedChanges(false);
      setChangeCount(0);
    } catch (error) {
      console.error('Error rejecting all changes:', error);
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;
      // Sync state after processing completes
      syncSuggestionState();
    }
  };

  if (!editor || !hasTrackedChanges) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{
          duration: 0.2,
          ease: 'easeOut',
        }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50"
      >
        <div className="bg-white/90 backdrop-blur-sm shadow-lg rounded-lg px-3 py-2 border border-gray-200/80 dark:bg-gray-900/90 dark:border-gray-700/80">
          <div className="flex items-center gap-3">
            {/* Navigation controls */}
            {changeCount > 1 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handlePreviousChange}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    title="Previous change"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                  </button>

                  <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 min-w-[60px] justify-center">
                    <span className="font-medium">
                      {currentChangeIndex + 1}
                    </span>
                    <span>/</span>
                    <span>{changeCount}</span>
                  </div>

                  <button
                    type="button"
                    onClick={handleNextChange}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    title="Next change"
                  >
                    <ChevronRight className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                <div className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
              </div>
            )}

            {/* Single change indicator */}
            {changeCount === 1 && (
              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium">1 change</span>
                </div>
                <div className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
              </div>
            )}

            {/* Individual accept/reject for current change */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleAcceptCurrent}
                className="p-1 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
                title="Accept this change"
                disabled={isProcessing}
              >
                <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </button>
              <button
                type="button"
                onClick={handleRejectCurrent}
                className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                title="Reject this change"
                disabled={isProcessing}
              >
                <X className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
              </button>
            </div>

            {changeCount > 1 && (
              <div className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
            )}

            {/* Bulk action buttons */}
            {changeCount > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="group flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 hover:text-green-800 hover:bg-green-50 rounded-md transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20"
                disabled={isProcessing}
              >
                <motion.div
                  animate={isProcessing ? { rotate: 360 } : { rotate: 0 }}
                  transition={{
                    duration: 1,
                    repeat: isProcessing ? Infinity : 0,
                    ease: 'linear',
                  }}
                >
                  <Check className="h-3.5 w-3.5" />
                </motion.div>
                <span>{isProcessing ? 'Processing' : 'Accept All'}</span>
              </button>

              <button
                type="button"
                onClick={handleRejectAll}
                className="group flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 hover:text-red-800 hover:bg-red-50 rounded-md transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                disabled={isProcessing}
              >
                <motion.div
                  animate={isProcessing ? { rotate: -360 } : { rotate: 0 }}
                  transition={{
                    duration: 1,
                    repeat: isProcessing ? Infinity : 0,
                    ease: 'linear',
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </motion.div>
                <span>{isProcessing ? 'Processing' : 'Reject All'}</span>
              </button>
            </div>
            )}
          </div>

          {/* Subtle progress indicator */}
          {isProcessing && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-blue-500 rounded-b-lg origin-left"
              transition={{ duration: changeCount * 0.05 }}
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
